import { Collapse } from '@backoffice-frontend/patterns'
import { ChevronDownIcon } from '@moia-dev/pace-icons'
import { Card, Heading, IconButton } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useControllableState } from './useControllableState'

type CollapsibleCardProps = {
  title: string
  children: ReactNode
  className?: string
  headerSlot?: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
}

export const CollapsibleCard = ({
  title,
  children,
  className,
  headerSlot,
  open,
  defaultOpen = true,
  onOpenChange,
  disabled = false,
}: CollapsibleCardProps) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useControllableState<boolean>({
    value: open,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  })

  const toggle = () => setIsOpen(prev => !prev)

  return (
    <Card padding="none" className={className} outline>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        onClick={disabled ? undefined : toggle}
        onKeyDown={
          disabled
            ? undefined
            : e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle()
                }
              }
        }
        css={theme => ({
          display: 'flex',
          padding: `${theme.space.Space2} ${theme.space.Space4}`,
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'default' : 'pointer',
        })}
      >
        <Heading size={'md'}>{title}</Heading>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space3,
          })}
        >
          {headerSlot}
          <div
            css={{ minWidth: 50, display: 'flex', justifyContent: 'center' }}
          >
            <IconButton
              variant="plain"
              size="compact"
              icon={
                <ChevronDownIcon
                  css={{
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              }
              label={
                isOpen
                  ? t('adFleetMap_card_collapse_label')
                  : t('adFleetMap_card_expand_label')
              }
              onClick={e => {
                e.stopPropagation()
                toggle()
              }}
              onKeyDown={e => e.stopPropagation()}
              tabIndex={-1}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <Collapse in={isOpen}>
        <div>{children}</div>
      </Collapse>
    </Card>
  )
}
